"use client"

import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { formatBlogDate } from "@/features/blog/lib/format-date"
import { blogPostPath, type UiLocale } from "@/shared/lib/i18n/config"
import type { BlogPost } from "@/shared/types/blog"

interface ArticleCardProps {
  post: BlogPost
  uiLocale: UiLocale
  variant?: "default" | "featured"
}

export function ArticleCard({ post, uiLocale, variant = "default" }: ArticleCardProps) {
  const t = useTranslations("blog.card")
  const isFeatured = variant === "featured"

  return (
    <article
      className={
        isFeatured
          ? "group blog-card blog-card-featured mb-12 border-b border-gray-200 pb-12 lg:grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-x-8"
          : "group blog-card"
      }
    >
      {/* Cover image */}
      {post.coverImage && (
        <Link href={blogPostPath(post.slug, uiLocale)} className="blog-card-image-link">
          <Image
            src={post.coverImage}
            alt={post.title}
            fill
            className="blog-card-image"
            sizes={
              isFeatured
                ? "(max-width: 1024px) 100vw, 52vw"
                : "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            }
            priority={isFeatured}
          />
        </Link>
      )}

      {/* Category */}
      {post.category && (
        <p className="blog-card-category">
          {post.category}
        </p>
      )}

      {/* Title */}
      <h3 className="mb-2.5">
        <Link
          href={blogPostPath(post.slug, uiLocale)}
          className="blog-card-title-link"
        >
          {post.title}
        </Link>
      </h3>

      {/* Excerpt */}
      <p className="blog-card-excerpt line-clamp-3">
        {post.excerpt}
      </p>

      {/* Author + date */}
      <div className="flex items-center gap-3">
        <div className="blog-card-avatar">
          <span className="text-[10px] font-bold text-white">
            {post.author ? post.author.charAt(0).toUpperCase() : "P"}
          </span>
        </div>
        <div className="blog-card-meta">
          {post.author && (
            <>
              <span className="font-medium text-[#333]">{post.author}</span>
              <span>&middot;</span>
            </>
          )}
          <time dateTime={post.publishedAt}>
            {formatBlogDate(post.publishedAt, "monthDay", uiLocale)}
          </time>
          {post.readTime > 0 && (
            <>
              <span>&middot;</span>
              <span>{t("readTime", { count: post.readTime })}</span>
            </>
          )}
        </div>
      </div>
    </article>
  )
}
